import { MenuGroupType, MenuGroup } from "types/menu-group";
import { ArticleStock, getMaxAvailableStock, hasEnoughStock } from "types/article-stock";
import { t } from "i18next";

interface ValidationResult {
    isValid: boolean;
    messageKey: string;
    messageParams?: Record<string, unknown>;
    helpTextKey?: string;
    helpTextParams?: Record<string, unknown>;
}

interface ValidationContext {
    menuGroup: MenuGroup;
    guests?: number;
    /**
     * The total quantity of items selected in the group. This is the sum of allQuantities.
     */
    totalGroupQuantities: number;
    /**
     * An array of the quantities of each item in the group.
     */
    allQuantities: number[];
    /**
     * Multiple choices group requires atleast one choice if this is false
     */
    optional: boolean;
    /**
     * Article stock information for validating stock availability
     */
    articleStocks?: Map<number, ArticleStock[]>;
}

const MenuValidators = {
    singelChoice: (context: ValidationContext): ValidationResult => {
        const { totalGroupQuantities } = context;
        const isValid = totalGroupQuantities === 1;

        return {
            isValid,
            messageKey: "preorder.menugroup.singleChoice",
            messageParams: {},
        };
    },

    /**
     * If not optional then atleast one item must be selected
     */
    multipleChoice: (context: ValidationContext): ValidationResult => {
        const { totalGroupQuantities, optional } = context;
        const isValid = optional || totalGroupQuantities > 0;

        return {
            isValid,
            messageKey: "preorder.menugroup.multipleChoice",
            messageParams: {},
        };
    },

    /**
     * Validates stock availability for AmountChoice group type
     */
    amountChoice: (context: ValidationContext): ValidationResult => {
        const { menuGroup, allQuantities, articleStocks } = context;

        if (!menuGroup.items || !articleStocks) {
            return { isValid: true, messageKey: "" };
        }

        // Check each item for stock availability
        for (let i = 0; i < menuGroup.items.length; i++) {
            const item = menuGroup.items[i];
            const requestedQuantity = allQuantities[i] || 0;

            if (
                requestedQuantity > 0 &&
                !hasEnoughStock(item.articleID, requestedQuantity, articleStocks)
            ) {
                const maxStock = getMaxAvailableStock(item.articleID, articleStocks);
                return {
                    isValid: false,
                    messageKey: t("preorder.menugroup.someChoicesExceeded"),
                    messageParams: { maxStock, index: i },
                };
            }
        }

        return { isValid: true, messageKey: "" };
    },

    /**
     * Validates that the number of selected item types does not exceed the allowed choices
     */
    limitedChoices: (context: ValidationContext): ValidationResult => {
        const { menuGroup, allQuantities } = context;
        const maximumConstraint = menuGroup.choices;
        const selectedItemsCount = allQuantities.filter((quantity) => quantity > 0).length;
        const isValid = selectedItemsCount <= maximumConstraint;

        return {
            isValid,
            messageKey: "preorder.menugroup.limitedChoicesMaxWarning",
            messageParams: { choices: maximumConstraint },
            ...(isValid
                ? {}
                : {
                      helpTextKey: "preorder.menugroup.limitedChoicesMaxHelpText",
                      helpTextParams: { remaining: maximumConstraint - selectedItemsCount },
                  }),
        };
    },

    /**
     * Validates that a minimum number of items are selected based on guest count
     */
    limitedMinChoicesToGuests: (context: ValidationContext): ValidationResult => {
        const { guests, totalGroupQuantities } = context;
        const minimumConstraint = guests || 1;
        const isValid = totalGroupQuantities >= minimumConstraint;
        const remaining = Math.max(0, minimumConstraint - totalGroupQuantities);

        return {
            isValid,
            messageKey: "preorder.menugroup.limitedMinChoicesToGuestsMinWarning",
            messageParams: { minimumConstraint },
            ...(isValid
                ? {}
                : {
                      helpTextKey: "preorder.menugroup.limitedMinChoicesToGuestsHelpText",
                      helpTextParams: { remaining },
                  }),
        };
    },

    /**
     * Validates that at least one option is selected for each guest
     */
    limitedMinMaxChoicesToGuests: (context: ValidationContext): ValidationResult => {
        const { guests, totalGroupQuantities, menuGroup } = context;
        const minimumConstraint = guests || 1;
        const isValid = totalGroupQuantities >= minimumConstraint;
        const remaining = Math.max(0, minimumConstraint - totalGroupQuantities);

        return {
            isValid,
            messageKey: "preorder.menugroup.limitedMinMaxChoicesToGuestsMinWarning",
            messageParams: { minimumConstraint, choices: menuGroup.choices },
            ...(isValid
                ? {}
                : {
                      helpTextKey: "preorder.menugroup.limitedMinMaxChoicesToGuestsHelpText",
                      helpTextParams: { remaining },
                  }),
        };
    },

    /**
     * Validates that the total quantity does not exceed the guest count
     */
    limitedToGuests: (context: ValidationContext): ValidationResult => {
        const { guests, totalGroupQuantities } = context;
        const guestCount = guests || 1;
        const isValid = totalGroupQuantities === guestCount;
        const remaining = Math.max(0, guestCount - totalGroupQuantities);

        return {
            isValid,
            messageKey: "preorder.menugroup.limitedToGuests",
            messageParams: { guests: guestCount },
            ...(isValid
                ? {}
                : {
                      helpTextKey: "preorder.menugroup.limitedToGuestsHelpText",
                      helpTextParams: { remaining },
                  }),
        };
    },

    /**
     * Validates that the customer has the same amount of items as guests but can only choose from choices type of items
     */
    limitedToGuestsAndChoices: (context: ValidationContext): ValidationResult => {
        const { guests, totalGroupQuantities, menuGroup, allQuantities } = context;
        const guestCount = guests || 1;
        const selectedItemsCount = allQuantities.filter((quantity) => quantity > 0).length;

        const hasCorrectItemCount = totalGroupQuantities === guestCount;
        const hasCorrectItemTypes = selectedItemsCount <= menuGroup.choices;
        const isValid = hasCorrectItemCount && hasCorrectItemTypes;
        const remaining = Math.max(0, guestCount - totalGroupQuantities);

        return {
            isValid,
            messageKey: "preorder.menugroup.limitedToGuestsAndChoices",
            messageParams: {
                guests: guestCount,
                choices: menuGroup.choices,
            },
            ...(isValid
                ? {}
                : {
                      helpTextKey: "preorder.menugroup.limitedToGuestsAndChoicesHelpText",
                      helpTextParams: { remaining },
                  }),
        };
    },
};

const validateMenuGroup = (
    groupType: MenuGroupType,
    context: ValidationContext
): ValidationResult => {
    switch (groupType) {
        case MenuGroupType.SingleChoice:
            return MenuValidators.singelChoice(context);
        case MenuGroupType.AmountChoice:
            return MenuValidators.amountChoice(context);
        case MenuGroupType.LimitedChoices:
            return MenuValidators.limitedChoices(context);
        case MenuGroupType.LimitedMinChoicesToGuests:
            return MenuValidators.limitedMinChoicesToGuests(context);
        case MenuGroupType.LimitedMinMaxChoicesToGuests:
            return MenuValidators.limitedMinMaxChoicesToGuests(context);
        case MenuGroupType.LimitedToGuests:
            return MenuValidators.limitedToGuests(context);
        case MenuGroupType.LimitedToGuestsAndChoices:
            return MenuValidators.limitedToGuestsAndChoices(context);
        default:
            return { isValid: true, messageKey: "" };
    }
};

export default validateMenuGroup;
export type { ValidationResult, ValidationContext };
